/**
 * ExecutingAgentsSection Component
 * Compact table with expandable rows for managing executing agents.
 * Desktop: full table. Mobile: stacked cards.
 * Inline detail + edit in expanded row.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  IdentificationIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  CheckIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { OathButton } from '../../shared/OathUI';
import { getExecutingAgents, updateExecutingAgentProfile } from '../../../services/oathUserService';
import { getAllStates } from '../../../services/oathLocationService';
import { formatPhoneNumber, stripPhoneFormatting } from './adminUtils';
import { toast } from 'react-toastify';

const PAGE_SIZE = 15;

// ============================================
// Shared helpers
// ============================================
const formatPhone = (value) => {
  if (!value) return null;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return value;
};

const formatAddressLines = (address) => {
  if (!address) return null;
  const street = [address.street1, address.street2].filter(Boolean).join(', ');
  const cityStateZip = [[address.city, address.state].filter(Boolean).join(', '), address.zipCode].filter(Boolean).join(' ');
  return [street, cityStateZip].filter(Boolean);
};

const formatAddressOneLiner = (address) => {
  const lines = formatAddressLines(address);
  return lines && lines.length > 0 ? lines.join(', ') : null;
};

const inputClass = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all';
const inputErrorClass = 'w-full px-2.5 py-1.5 border border-red-400 dark:border-red-500 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-200 focus:border-red-400 transition-all';
const emptyAddress = { street1: '', street2: '', city: '', state: '', zipCode: '' };

/**
 * Validate an address: if any required field is filled, all required fields must be filled.
 * street2 is always optional.
 * Returns object with field names as keys and error messages as values, or empty object if valid.
 */
const validateAddress = (address) => {
  if (!address) return {};
  const required = { street1: address.street1, city: address.city, state: address.state, zipCode: address.zipCode };
  const hasAny = Object.values(required).some(v => v && v.trim());
  if (!hasAny) return {};
  const errors = {};
  if (!required.street1?.trim()) errors.street1 = 'Required';
  if (!required.city?.trim()) errors.city = 'Required';
  if (!required.state?.trim()) errors.state = 'Required';
  if (!required.zipCode?.trim()) errors.zipCode = 'Required';
  return errors;
};

const STATUS_LABELS = {
  AC: 'Active',
  IN: 'Inactive',
  SU: 'Suspended',
  TRM: 'Terminated',
};

// ============================================
// Stat Pill
// ============================================
const StatPill = ({ label, value, variant = 'default', selected = false, onClick }) => {
  const variants = {
    default: selected
      ? 'bg-gray-300 dark:bg-gray-500 text-gray-900 dark:text-white ring-2 ring-gray-400 dark:ring-gray-400'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    active: selected
      ? 'bg-green-200 dark:bg-green-800/50 text-green-800 dark:text-green-200 ring-2 ring-green-400 dark:ring-green-500'
      : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    inactive: selected
      ? 'bg-gray-300 dark:bg-gray-500 text-gray-900 dark:text-white ring-2 ring-gray-400 dark:ring-gray-400'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${variants[variant]}`}
    >
      <span>{label}</span>
      <span className="font-bold">{value}</span>
    </button>
  );
};

// ============================================
// Search input
// ============================================
const SearchInput = ({ placeholder, value, onChange }) => (
  <div className="relative">
    <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="pl-8 pr-3 py-1.5 w-full sm:w-56 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
    />
  </div>
);

// ============================================
// Address block (read mode)
// ============================================
const AddressBlock = ({ label, address }) => {
  const lines = formatAddressLines(address);
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</p>
      <div className="flex items-start gap-1.5 mt-0.5">
        <MapPinIcon className="h-3.5 w-3.5 text-rose-400 dark:text-rose-400 flex-shrink-0 mt-0.5" />
        {lines && lines.length > 0 ? (
          <div className="text-sm text-gray-900 dark:text-white leading-snug">
            {lines.map((line, i) => <p key={i}>{line}</p>)}
          </div>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500">-</p>
        )}
      </div>
    </div>
  );
};

// ============================================
// State typeahead (edit mode)
// ============================================
const StateTypeahead = ({ value, onChange, states = [], hasError = false }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const filtered = query
    ? states.filter((s) =>
        s.stateCd.toLowerCase().startsWith(query.toLowerCase()) ||
        s.stateName.toLowerCase().startsWith(query.toLowerCase())
      )
    : states;

  const displayValue = value || '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query) {
      setHighlightIndex(0);
    } else {
      const idx = filtered.findIndex((s) => s.stateCd === value);
      setHighlightIndex(idx >= 0 ? idx : 0);
    }
  }, [query, filtered, value]);

  const handleSelect = (stateCd) => {
    onChange(stateCd);
    setQuery('');
    setOpen(false);
    const container = ref.current?.closest('.grid');
    if (container) {
      const inputs = [...container.querySelectorAll('input')];
      const currentIdx = inputs.indexOf(inputRef.current);
      if (currentIdx >= 0 && inputs[currentIdx + 1]) {
        inputs[currentIdx + 1].focus();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) handleSelect(filtered[highlightIndex].stateCd);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <input
        ref={inputRef}
        type="text"
        value={open ? query : displayValue}
        placeholder="State"
        autoComplete="off"
        data-1p-ignore
        data-lpignore="true"
        className={hasError ? inputErrorClass : inputClass}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={handleKeyDown}
      />
      <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
      {open && (
        <div className="absolute z-20 bottom-full mb-1 w-full max-h-40 overflow-y-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <p className="px-2.5 py-1.5 text-xs text-gray-400">No matches</p>
          ) : (
            filtered.map((s, i) => (
              <button
                key={s.stateCd}
                type="button"
                ref={i === highlightIndex ? (el) => { if (el && !query) el.scrollIntoView({ block: 'nearest' }); } : undefined}
                onClick={() => handleSelect(s.stateCd)}
                className={`w-full text-left px-2.5 py-1.5 text-sm transition-colors ${
                  i === highlightIndex
                    ? 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light'
                    : s.stateCd === value
                      ? 'bg-primary/5 dark:bg-primary/10 text-primary dark:text-primary-light font-medium'
                      : 'text-gray-900 dark:text-white hover:bg-primary/10 dark:hover:bg-primary/20'
                }`}
              >
                {s.stateCd} — {s.stateName}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// Address form (edit mode)
// ============================================
const AddressForm = ({ label, address, onChange, states = [], actionButton, errors = {} }) => {
  const hasErrors = Object.keys(errors).length > 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</p>
          {hasErrors && <p className="text-[10px] text-red-500 dark:text-red-400">Fill all required fields</p>}
        </div>
        {actionButton}
      </div>
      <div className="space-y-2">
        <input type="text" placeholder="Street 1" autoComplete="off" data-1p-ignore data-lpignore="true" value={address.street1 || ''} onChange={(e) => onChange({ ...address, street1: e.target.value })} className={errors.street1 ? inputErrorClass : inputClass} />
        <input type="text" placeholder="Street 2 (optional)" autoComplete="off" data-1p-ignore data-lpignore="true" value={address.street2 || ''} onChange={(e) => onChange({ ...address, street2: e.target.value })} className={inputClass} />
        <div className="grid grid-cols-3 gap-2">
          <input type="text" placeholder="City" autoComplete="off" data-1p-ignore data-lpignore="true" value={address.city || ''} onChange={(e) => onChange({ ...address, city: e.target.value })} className={errors.city ? inputErrorClass : inputClass} />
          <StateTypeahead value={address.state} onChange={(stateCd) => onChange({ ...address, state: stateCd })} states={states} hasError={!!errors.state} />
          <input type="text" placeholder="Zip" autoComplete="off" data-1p-ignore data-lpignore="true" value={address.zipCode || ''} onChange={(e) => onChange({ ...address, zipCode: e.target.value })} className={errors.zipCode ? inputErrorClass : inputClass} />
        </div>
      </div>
    </div>
  );
};

// ============================================
// Skeleton loader (table-style with shimmer)
// ============================================
const ShimmerBar = ({ className }) => (
  <div className={`rounded bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200%_100%] animate-shimmer ${className}`} />
);

const TableSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0" style={{ animationDelay: `${i * 75}ms` }}>
        <ShimmerBar className="w-7 h-7 rounded-lg flex-shrink-0" />
        <div className="flex-1 flex items-center gap-6">
          <ShimmerBar className="h-4 w-32" />
          <ShimmerBar className="h-4 w-14" />
          <ShimmerBar className="h-4 w-40 hidden sm:block" />
          <ShimmerBar className="h-4 w-28 hidden md:block" />
        </div>
      </div>
    ))}
  </div>
);

// ============================================
// Compact avatar (shared by table row + mobile card)
// ============================================
const AgentAvatar = ({ agent }) => {
  const isActive = agent.statusCd === 'AC';
  const initials = `${agent.firstName?.charAt(0) || ''}${agent.lastName?.charAt(0) || ''}`.toUpperCase();
  return (
    <div className={`w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${
      isActive
        ? 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
    }`}>
      {initials || '?'}
    </div>
  );
};

// ============================================
// Error state
// ============================================
const ErrorState = ({ message, onRetry }) => (
  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
    <ExclamationTriangleIcon className="h-10 w-10 text-red-400 dark:text-red-500 mx-auto mb-3" />
    <h3 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">Failed to Load</h3>
    <p className="text-sm text-red-600 dark:text-red-400 mb-4">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
      >
        <ArrowPathIcon className="h-4 w-4" />
        Try Again
      </button>
    )}
  </div>
);

// ============================================
// Expanded detail / edit panel (shown below row)
// ============================================
const ExpandedDetail = ({ agent, agencyId, canEdit, isSelf, onSaved, states, onLoadStates }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const initialFormData = useRef(null);

  const hasChanges = useMemo(() => {
    if (!initialFormData.current) return false;
    return JSON.stringify(formData) !== JSON.stringify(initialFormData.current);
  }, [formData]);

  const physicalAddressErrors = useMemo(() => validateAddress(formData.physicalAddress), [formData.physicalAddress]);
  const mailingAddressErrors = useMemo(() => validateAddress(formData.mailingAddress), [formData.mailingAddress]);
  const hasValidationErrors = Object.keys(physicalAddressErrors).length > 0 || Object.keys(mailingAddressErrors).length > 0;

  const startEditing = async () => {
    if (states.length === 0) await onLoadStates();
    const initial = {
      statusCd: agent.statusCd || '',
      email: agent.email || '',
      phone: formatPhoneNumber(agent.phone || ''),
      mobile: formatPhoneNumber(agent.mobile || ''),
      fax: formatPhoneNumber(agent.fax || ''),
      physicalAddress: { ...emptyAddress, ...agent.physicalAddress },
      mailingAddress: { ...emptyAddress, ...agent.mailingAddress },
    };
    initialFormData.current = initial;
    setFormData({ ...initial });
    setIsEditing(true);
  };

  const handlePhoneChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: formatPhoneNumber(value) }));
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      statusCd: formData.statusCd || null,
      email: formData.email || null,
      phone: stripPhoneFormatting(formData.phone) || null,
      mobile: stripPhoneFormatting(formData.mobile) || null,
      fax: stripPhoneFormatting(formData.fax) || null,
      physicalAddress: formData.physicalAddress,
      mailingAddress: formData.mailingAddress,
    };
    const result = await updateExecutingAgentProfile(agencyId, agent.producerId, payload);
    setSaving(false);

    if (result.success) {
      toast.success('Executing agent profile updated');
      setIsEditing(false);
      if (onSaved) onSaved();
    } else {
      toast.error(result.error || 'Failed to update profile');
    }
  };

  if (isEditing) {
    return (
      <form autoComplete="off" onSubmit={(e) => e.preventDefault()} className="space-y-4">
        {/* Status */}
        <div className="max-w-xs">
          <label className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Status</label>
          <select
            value={formData.statusCd}
            onChange={(e) => setFormData({ ...formData, statusCd: e.target.value })}
            className={inputClass + ' appearance-none'}
          >
            {Object.entries(STATUS_LABELS).filter(([code]) => code !== 'TRM').map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>

        {/* Contact fields */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Email</label>
            <input type="email" autoComplete="off" data-1p-ignore data-lpignore="true" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputClass} placeholder="email@example.com" />
          </div>
          <div>
            <label className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Phone</label>
            <input type="tel" autoComplete="off" data-1p-ignore data-lpignore="true" value={formData.phone} onChange={(e) => handlePhoneChange('phone', e.target.value)} className={inputClass} placeholder="(555) 555-5555" />
          </div>
          <div>
            <label className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Mobile</label>
            <input type="tel" autoComplete="off" data-1p-ignore data-lpignore="true" value={formData.mobile} onChange={(e) => handlePhoneChange('mobile', e.target.value)} className={inputClass} placeholder="(555) 555-5555" />
          </div>
          <div>
            <label className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Fax</label>
            <input type="tel" autoComplete="off" data-1p-ignore data-lpignore="true" value={formData.fax} onChange={(e) => handlePhoneChange('fax', e.target.value)} className={inputClass} placeholder="(555) 555-5555" />
          </div>
        </div>

        {/* Addresses */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <AddressForm label="Physical Address" address={formData.physicalAddress} onChange={(addr) => setFormData({ ...formData, physicalAddress: addr })} states={states} errors={physicalAddressErrors} />
          <AddressForm
            label="Mailing Address"
            address={formData.mailingAddress}
            onChange={(addr) => setFormData({ ...formData, mailingAddress: addr })}
            states={states}
            errors={mailingAddressErrors}
            actionButton={
              <button
                type="button"
                onClick={() => setFormData({ ...formData, mailingAddress: { ...formData.physicalAddress } })}
                className="inline-flex items-center gap-1 text-[10px] text-primary dark:text-primary-light hover:text-primary-dark dark:hover:text-white transition-colors font-medium"
              >
                <DocumentDuplicateIcon className="h-3 w-3" />
                Same as physical
              </button>
            }
          />
        </div>

        {/* Save / Cancel */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-700/50">
          <OathButton variant="outline" size="sm" onClick={handleCancel} disabled={saving}>
            <XMarkIcon className="h-3.5 w-3.5" />
            Cancel
          </OathButton>
          <OathButton size="sm" onClick={handleSave} loading={saving} disabled={!hasChanges || hasValidationErrors}>
            <CheckIcon className="h-3.5 w-3.5" />
            {saving ? 'Saving...' : 'Save'}
          </OathButton>
        </div>
      </form>
    );
  }

  // Read mode
  return (
    <div className="space-y-4">
      {/* Contact info card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 animate-fade-in-up">
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Contact Information</p>
          {canEdit && !isSelf && (
            <OathButton variant="outline" size="sm" onClick={startEditing}>
              <PencilSquareIcon className="h-3.5 w-3.5" />
              Edit
            </OathButton>
          )}
          {isSelf && (
            <span className="text-[10px] text-gray-400 dark:text-gray-500 italic">You cannot edit your own profile</span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <EnvelopeIcon className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
              <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Email</p>
            </div>
            <p className="text-sm text-gray-900 dark:text-white truncate mt-0.5 pl-5">{agent.email || '-'}</p>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <PhoneIcon className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
              <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Phone</p>
            </div>
            <p className="text-sm text-gray-900 dark:text-white truncate mt-0.5 pl-5">{formatPhone(agent.phone) || '-'}</p>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <DevicePhoneMobileIcon className="h-3.5 w-3.5 text-violet-500 dark:text-violet-400 flex-shrink-0" />
              <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Mobile</p>
            </div>
            <p className="text-sm text-gray-900 dark:text-white truncate mt-0.5 pl-5">{formatPhone(agent.mobile) || '-'}</p>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <PhoneIcon className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
              <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Fax</p>
            </div>
            <p className="text-sm text-gray-900 dark:text-white truncate mt-0.5 pl-5">{formatPhone(agent.fax) || '-'}</p>
          </div>
        </div>
      </div>

      {/* Address cards — always show both */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 animate-fade-in-up" style={{ animationDelay: '75ms' }}>
          <AddressBlock label="Physical Address" address={agent.physicalAddress} />
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 animate-fade-in-up" style={{ animationDelay: '150ms' }}>
          <AddressBlock label="Mailing Address" address={agent.mailingAddress} />
        </div>
      </div>
    </div>
  );
};

// ============================================
// Status badge (shared between table row & card)
// ============================================
const StatusBadge = ({ statusCd }) => {
  const isActive = statusCd === 'AC';
  return (
    <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded ${
      isActive
        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
    }`}>
      {STATUS_LABELS[statusCd] || statusCd}
    </span>
  );
};

// ============================================
// Pagination (lightweight, local-only)
// ============================================
const Pagination = ({ currentPage, totalPages, totalCount, onPageChange }) => {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-700 dark:text-gray-300">{startItem}</span>
        {' '}&ndash;{' '}
        <span className="font-medium text-gray-700 dark:text-gray-300">{endItem}</span>
        {' '}of{' '}
        <span className="font-medium text-gray-700 dark:text-gray-300">{totalCount}</span>
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        </button>
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 px-2">
          {currentPage} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRightIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// Desktop table row
// ============================================
const AgentTableRow = ({ agent, isExpanded, onToggle, canEdit, isSelf, onSaved, agencyId, states, onLoadStates }) => {
  const isActive = agent.statusCd === 'AC';

  return (
    <>
      <tr
        onClick={onToggle}
        className={`cursor-pointer transition-all duration-150 ${
          isExpanded
            ? 'bg-primary/5 dark:bg-primary/10'
            : 'hover:bg-primary/[0.03] dark:hover:bg-primary/[0.06] hover:shadow-[inset_3px_0_0_0_theme(colors.primary)]'
        } ${!isActive && !isExpanded ? 'opacity-60 hover:opacity-90' : ''}`}
      >
        {/* Chevron */}
        <td className="pl-4 pr-1 py-2.5 w-8">
          <ChevronRightIcon className={`h-3.5 w-3.5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
        </td>
        {/* Name */}
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2.5">
            <AgentAvatar agent={agent} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {agent.fullName || `${agent.firstName} ${agent.lastName}`}
              </p>
            </div>
          </div>
        </td>
        {/* Badges */}
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5">
            <StatusBadge statusCd={agent.statusCd} />
            {agent.isPrimary && (
              <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                Primary
              </span>
            )}
            {isSelf && (
              <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">You</span>
            )}
          </div>
        </td>
        {/* Email */}
        <td className="px-3 py-2.5 hidden md:table-cell">
          <p className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[200px]">{agent.email || '-'}</p>
        </td>
        {/* Phone */}
        <td className="px-3 py-2.5 hidden lg:table-cell">
          <p className="text-sm text-gray-600 dark:text-gray-300">{formatPhone(agent.phone) || '-'}</p>
        </td>
        {/* Location */}
        <td className="px-3 py-2.5 pr-4 hidden xl:table-cell">
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
            {formatAddressOneLiner(agent.physicalAddress) || '-'}
          </p>
        </td>
      </tr>

      {/* Expanded detail */}
      {isExpanded && (
        <tr>
          <td colSpan={6} className="px-5 py-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 border-l-2 border-l-primary animate-expand-down">
            <ExpandedDetail
              agent={agent}
              agencyId={agencyId}
              canEdit={canEdit}
              isSelf={isSelf}
              onSaved={onSaved}
              states={states}
              onLoadStates={onLoadStates}
            />
          </td>
        </tr>
      )}
    </>
  );
};

// ============================================
// Mobile card (stacked)
// ============================================
const AgentMobileCard = ({ agent, isExpanded, onToggle, canEdit, isSelf, onSaved, agencyId, states, onLoadStates }) => {
  const isActive = agent.statusCd === 'AC';

  return (
    <div className={`border-b border-gray-200 dark:border-gray-700 last:border-b-0 ${!isActive && !isExpanded ? 'opacity-60' : ''}`}>
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 ${
          isExpanded ? 'bg-primary/5 dark:bg-primary/10' : 'hover:bg-primary/[0.03] dark:hover:bg-primary/[0.06] hover:pl-5'
        }`}
      >
        <ChevronRightIcon className={`h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        <AgentAvatar agent={agent} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {agent.fullName || `${agent.firstName} ${agent.lastName}`}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{agent.email || '-'}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <StatusBadge statusCd={agent.statusCd} />
          {agent.isPrimary && (
            <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
              Primary
            </span>
          )}
          {isSelf && (
            <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">You</span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pt-3 bg-gray-50 dark:bg-gray-900/50 border-l-2 border-l-primary animate-expand-down">
          <ExpandedDetail
            agent={agent}
            agencyId={agencyId}
            canEdit={canEdit}
            onSaved={onSaved}
            states={states}
            onLoadStates={onLoadStates}
          />
        </div>
      )}
    </div>
  );
};

// ============================================
// Main Section
// ============================================
const ExecutingAgentsSection = ({ agency, canEdit, currentProducerId, onCountLoaded }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [states, setStates] = useState([]);

  // Persist expanded agent in URL
  const expandedIdParam = searchParams.get('agent');
  const expandedId = expandedIdParam ? parseInt(expandedIdParam, 10) : null;

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await getExecutingAgents(agency.agencyId);
    if (result.success) {
      const list = result.agents || [];
      setAgents(list);
      if (onCountLoaded) onCountLoaded(list.filter(a => a.statusCd === 'AC').length);
    } else {
      setError(result.error);
    }
    setLoading(false);
  }, [agency.agencyId, onCountLoaded]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const loadStates = useCallback(async () => {
    if (states.length > 0) return;
    const result = await getAllStates();
    if (result.success) setStates(result.data);
  }, [states.length]);

  const stats = useMemo(() => {
    const active = agents.filter((a) => a.statusCd === 'AC').length;
    const inactive = agents.length - active;
    return { total: agents.length, active, inactive };
  }, [agents]);

  // Filter + sort
  const filteredAgents = useMemo(() => {
    let filtered = agents;
    if (activeFilter !== null) {
      filtered = filtered.filter((a) => (a.statusCd === 'AC') === activeFilter);
    }
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((a) =>
        a.firstName?.toLowerCase().includes(searchLower) ||
        a.lastName?.toLowerCase().includes(searchLower) ||
        a.fullName?.toLowerCase().includes(searchLower) ||
        a.email?.toLowerCase().includes(searchLower)
      );
    }
    return [...filtered].sort((a, b) => {
      // Primary first
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      // Then active before inactive
      const aActive = a.statusCd === 'AC' ? 0 : 1;
      const bActive = b.statusCd === 'AC' ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      const nameA = (a.fullName || `${a.firstName} ${a.lastName}`).toLowerCase();
      const nameB = (b.fullName || `${b.firstName} ${b.lastName}`).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [agents, search, activeFilter]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredAgents.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedAgents = filteredAgents.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // On initial load, jump to the page containing the pre-selected agent
  const initialJumpDone = useRef(false);
  useEffect(() => {
    if (initialJumpDone.current || !expandedId || filteredAgents.length === 0) return;
    const idx = filteredAgents.findIndex((a) => a.producerId === expandedId);
    if (idx >= 0) {
      setCurrentPage(Math.floor(idx / PAGE_SIZE) + 1);
    }
    initialJumpDone.current = true;
  }, [filteredAgents, expandedId]);

  // Reset page on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeFilter]);

  // Collapse expanded row when page changes
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('agent');
      return next;
    }, { replace: true });
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpanded = (agentId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (expandedId === agentId) {
        next.delete('agent');
      } else {
        next.set('agent', String(agentId));
      }
      return next;
    }, { replace: true });
  };

  return (
    <div className="space-y-5">
      {/* Stats + Search */}
      {!loading && agents.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <StatPill label="Total" value={stats.total} selected={activeFilter === null} onClick={() => setActiveFilter(null)} />
            <StatPill label="Active" value={stats.active} variant="active" selected={activeFilter === true} onClick={() => setActiveFilter((f) => f === true ? null : true)} />
            {stats.inactive > 0 && (
              <StatPill label="Inactive" value={stats.inactive} variant="inactive" selected={activeFilter === false} onClick={() => setActiveFilter((f) => f === false ? null : false)} />
            )}
          </div>
          <SearchInput placeholder="Search agents..." value={search} onChange={setSearch} />
        </div>
      )}

      {/* Content */}
      {loading ? (
        <TableSkeleton />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchAgents} />
      ) : filteredAgents.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-10 text-center">
          <div className="w-14 h-14 bg-primary/10 dark:bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <IdentificationIcon className="h-7 w-7 text-primary dark:text-primary-light" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
            {search ? 'No matching agents' : 'No executing agents'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
            {search ? 'Try adjusting your search term.' : 'No executing agents are configured for this agency.'}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Desktop table (hidden on small screens) */}
          <div className="hidden sm:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                  <th className="pl-4 pr-1 py-2 w-8" />
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Email</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">Phone</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider pr-4 hidden xl:table-cell">Location</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {pagedAgents.map((agent) => (
                  <AgentTableRow
                    key={agent.producerId}
                    agent={agent}
                    isExpanded={expandedId === agent.producerId}
                    onToggle={() => toggleExpanded(agent.producerId)}
                    canEdit={canEdit}
                    isSelf={!!currentProducerId && currentProducerId === agent.producerId}
                    onSaved={fetchAgents}
                    agencyId={agency.agencyId}
                    states={states}
                    onLoadStates={loadStates}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards (visible on small screens) */}
          <div className="sm:hidden">
            {pagedAgents.map((agent) => (
              <AgentMobileCard
                key={agent.producerId}
                agent={agent}
                isExpanded={expandedId === agent.producerId}
                onToggle={() => toggleExpanded(agent.producerId)}
                canEdit={canEdit}
                isSelf={!!currentProducerId && currentProducerId === agent.producerId}
                onSaved={fetchAgents}
                agencyId={agency.agencyId}
                states={states}
                onLoadStates={loadStates}
              />
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            totalCount={filteredAgents.length}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default ExecutingAgentsSection;
